import type { UserInfo } from "../services/types/user-types";
import type { Language } from "../shared/types/websiteTypes";
import type { SelectChangeEvent, Theme } from "@mui/material";
import type {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  SyntheticEvent,
} from "react";

import { Routes } from "../navigation/navigation-types";

import { makeStyles } from "@mui/styles";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  getActiveWebId,
  getActiveWebUrl,
  getUserWebs,
  getPixelStatus,
} from "../services/selectors/web-selectors";
import { getUserInfo } from "../services/selectors/user-selectors";

import { WebsiteManager } from "../services/api/WebsiteManager";
import { showFlashMessageWithTimeout } from "../services/actions/flashMessage-actions";
import {
  setActiveWeb,
  setCustomWebLogo,
  setWebs,
  updateWeb,
  setPixelStatus,
} from "../services/actions/web-actions";
import { stringIncludesSubstring } from "../generic-utils";
import { UpdateWebsiteResponse } from "../shared/payloads/websitePayloads";
import { useTranslation } from "react-i18next";
import { UserManager } from "../services/api/UserManager";

type SettingsTabs = "web_settings" | "custom_branding";

export interface WebsiteLogo {
  logo?: string;
  logoDisplay: boolean;
  logoBackgroundColor?: string;
  logoText?: string;
}

interface UseSettings {
  classes: any;
  webName: string;
  webUrl: string;
  user: UserInfo;
  selectedLanguage: Language;
  selectedTab: SettingsTabs;
  isFetching: boolean;
  isConnected: boolean;
  isCheckLoading: boolean;
  isPixelModalOpen: boolean;
  isAddWebModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isDeleting: boolean;
  webLogo: WebsiteLogo;
  setIsAddWebModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsPixelModalOpen: Dispatch<SetStateAction<boolean>>;
  handleWebNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleWebUrlChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSettingsTabChange: (_: SyntheticEvent, newValue: string) => void;
  handleDeleteWeb: () => void;
  handleUpdateLogo: (webLogo: WebsiteLogo) => Promise<void>;
  onLanguageChange: (event: SelectChangeEvent) => void;
  checkPixelStatus: () => void;
}

export const useSettings = (checkPixel = true): UseSettings => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const activeWebId = useSelector(getActiveWebId);
  const activeWebUrl = useSelector(getActiveWebUrl);
  const webs = useSelector(getUserWebs);
  const user = useSelector(getUserInfo);
  const pixelConnected = useSelector(getPixelStatus);
  const { t } = useTranslation();

  const THIRTY_MINUTES = 30 * 60 * 1000;

  const [webName, setWebName] = useState<string>("");
  const [webUrl, setWebUrl] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<Language>("cs");
  const [selectedTab, setSelectedTab] = useState<SettingsTabs>(
    user?.registeredFrom === "internal" ? "web_settings" : "custom_branding",
  );
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isPixelModalOpen, setIsPixelModalOpen] = useState<boolean>(false);
  const [isAddWebModalOpen, setIsAddWebModalOpen] = useState<boolean>(false);
  const [isCheckLoading, setIsCheckLoading] = useState<boolean>(false);
  const [webLogo, setWebLogo] = useState<WebsiteLogo>({
    logoDisplay: true,
    logo: undefined,
    logoBackgroundColor: undefined,
    logoText: undefined,
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  useEffect(() => {
    if (!checkPixel) {
      return;
    }

    checkPixelStatus();

    const intervalId = setInterval(() => {
      checkPixelStatus();
    }, THIRTY_MINUTES);

    return () => clearInterval(intervalId);
  }, [webUrl]);

  useEffect(() => {
    const fetchWebInfo = async () => {
      try {
        if (activeWebId) {
          setIsFetching(true);
          const _websiteManager = WebsiteManager.getManager();
          const result = await _websiteManager.getWebsiteSingleAsync(
            activeWebId,
          );
          setWebName(result.name);
          setWebUrl(result.url);
          setSelectedLanguage(result.language);
          setWebLogo({
            logoDisplay: result?.logoDisplay,
            logo: result?.logo,
            logoBackgroundColor: result?.logoBackgroundColor,
            logoText: result?.logoText,
          });
          dispatch(
            setCustomWebLogo({
              logoDisplay: result?.logoDisplay,
              logo: result?.logo,
              logoBackgroundColor: result?.logoBackgroundColor,
              logoText: result?.logoText,
            }),
          );
          setIsFetching(false);
        }
      } catch (e: any) {
        showFlashMessageWithTimeout(dispatch, e.toString(), "error");
        setIsFetching(false);
      }
    };

    fetchWebInfo();
  }, [webs, dispatch, activeWebId]);

  const handleSettingsTabChange = (_: SyntheticEvent, value: string) => {
    setSelectedTab(value as SettingsTabs);
  };

  const checkPixelStatus = async () => {
    if (activeWebUrl && activeWebId && webUrl) {
      setIsCheckLoading(true);
      try {
        const _websiteManager = WebsiteManager.getManager();
        const _userManager = UserManager.getManager();

        const result = await _websiteManager.getWebsiteHtmlAsync(activeWebUrl);
        if (stringIncludesSubstring(result.html, activeWebId)) {
          dispatch(setPixelStatus(true));
          if (user.hasActivatedPixel === false) {
            await _userManager.activeUserPixelScript();
          }
        } else {
          dispatch(setPixelStatus(false));
          showFlashMessageWithTimeout(
            dispatch,
            t("errors.pixelCheckFailed"),
            "error",
          );
        }
        setIsCheckLoading(false);
      } catch (e: any) {
        setIsCheckLoading(false);
        // Don't change connection status on network errors, keep it as optimistic
        // Only show error if it's not a network connectivity issue
        console.warn("Pixel check failed due to network error:", e);
      }
    }
  };

  const handleWebNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWebName(event.target.value);
  };

  const handleWebUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWebUrl(event.target.value);
  };

  const onLanguageChange = (event: SelectChangeEvent) => {
    setSelectedLanguage(event.target.value as Language);
  };

  const handleDeleteWeb = async () => {
    if (activeWebId) {
      try {
        setIsDeleting(true);
        const _websiteManager = WebsiteManager.getManager();
        await _websiteManager.deleteWebsiteAsync(activeWebId);
        const filteredWebs = webs.filter(web => web.id !== activeWebId);
        if (filteredWebs.length >= 1) {
          dispatch(setActiveWeb(filteredWebs[0].url, filteredWebs[0].id));
          dispatch(setWebs(filteredWebs));
          navigate(Routes.websites);
        } else {
          navigate(Routes.onboarding);
        }
        setIsDeleting(false);
      } catch (e: any) {
        showFlashMessageWithTimeout(dispatch, e.toString(), "error");
        setIsDeleting(false);
      }
    }
  };

  const handleUpdateLogo = async (webLogo: WebsiteLogo) => {
    const _websiteManager = WebsiteManager.getManager();

    if (!activeWebId) {
      return;
    }

    try {
      const result: UpdateWebsiteResponse =
        await _websiteManager.updateWebsiteAsync(
          activeWebId,
          webName,
          webUrl,
          selectedLanguage,
          webLogo,
        );

      if (result) {
        dispatch(updateWeb(activeWebId, webName, webUrl, selectedLanguage));
        dispatch(setActiveWeb(webUrl, activeWebId));
        dispatch(setCustomWebLogo(webLogo));
        showFlashMessageWithTimeout(
          dispatch,
          t("popups.changesSaved"),
          "success",
        );
      }
    } catch (e: any) {
      // Parse error to provide user-friendly messages
      let errorMessage = e.toString();

      // Check if error is an object with file size limit info
      if (typeof e === "object" && e !== null) {
        if (e.type === "entity.too.large" || e.message === "request entity too large") {
          // Calculate sizes in KB
          const maxSizeKB = e.limit ? Math.round(e.limit / 1024) : 100;
          const actualSizeKB = e.length ? Math.round(e.length / 1024) : 0;

          if (actualSizeKB > 0) {
            errorMessage = t("errors.logoFileTooLarge", {
              maxSize: maxSizeKB,
              actualSize: actualSizeKB,
            });
          } else {
            errorMessage = t("errors.logoFileTooLargeSimple");
          }
        }
      }

      showFlashMessageWithTimeout(dispatch, errorMessage, "error");
    }
  };

  return {
    classes,
    webName,
    webUrl,
    webLogo,
    user,
    selectedLanguage,
    selectedTab,
    isFetching,
    isCheckLoading,
    isConnected: pixelConnected,
    isPixelModalOpen,
    isAddWebModalOpen,
    isDeleteModalOpen,
    isDeleting,
    onLanguageChange,
    handleWebNameChange,
    handleUpdateLogo,
    handleWebUrlChange,
    handleDeleteWeb,
    handleSettingsTabChange,
    setIsAddWebModalOpen,
    setIsDeleteModalOpen,
    setIsPixelModalOpen,
    checkPixelStatus,
  };
};

const useStyles: any = makeStyles((theme: Theme) => ({
  loadingContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 32,
  },
  container: {
    marginTop: 16,
    marginLeft: 24,
    marginRight: 24,
  },
  topBarContainer: {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
  },
  redText: {
    fontSize: 16,
    color: theme.palette.main.alert.o,
    marginRight: 44,
    cursor: "pointer",
  },
  webName: {
    fontSize: 28,
    fontWeight: "bold",
    color: theme.palette.main.secondary.u1,
    height: 42,
  },
  statusContainer: {
    height: 76,
    flex: 1,
    backgroundColor: theme.palette.natural.t6,
    border: `1px solid ${theme.palette.natural.t8}`,
    display: "flex",
    alignItems: "center",
    marginTop: 24,
    borderRadius: 4,
  },
  statusRow: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusTitle: {
    fontSize: 16,
    color: theme.palette.natural.t1,
    marginLeft: 16,
  },
}));
